import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import { useQueryClient } from 'react-query';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { PrimaryButton, SecondaryOutlineButton } from 'Core/components/Button';
import InputField from 'Core/components/FormElements/InputField';
import Loader from 'Core/components/Loader';
import { AlertError } from 'Core/components/Alert';
import Portal from 'Core/components/Portal';
import {
  FaPhoneAlt,
  FaEye,
  FaEyeSlash,
  FaSnowflake,
  FaExclamationTriangle,
  FaUserCheck,
} from 'react-icons/fa';
import { useNavigate } from 'react-location';

import useRequalifyApplication from 'Modules/fortiva/hooks/useRequalifyApplication';
import useRequalifyForm from 'Modules/fortiva/hooks/useRequalifyForm';
import useGetApplication from 'Modules/fortiva/hooks/useGetApplication';
import { createFinancingPath } from 'Modules/fortiva/routes';
import constants from 'Modules/fortiva/config/constants';
import cacheKeys from 'Core/hooks/queries/cacheKeys';

const PhoneCard = ({ title, phone, description }) => (
  <a
    href={`tel:${phone}`}
    className="block p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 hover:shadow-md transition-all duration-200 group"
  >
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <FaPhoneAlt className="text-blue-600" />
      </div>
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-blue-600 font-semibold">{phone}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </a>
);

const ConfirmationModal = ({ isOpen, onConfirm, onCancel, t }) => {
  if (!isOpen) return null;

  return (
    <Portal>
      <div className="z-50 inset-0 fixed items-center justify-center flex bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 overflow-hidden animate-in fade-in zoom-in duration-200">
          {/* Warning Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-white text-2xl" />
              </div>
              <h2 className="text-xl font-bold text-white">{t('fortiva.requalify.modal.title')}</h2>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-md mb-6">
              <p className="text-amber-800 font-medium">
                {t('fortiva.requalify.modal.warningMessage')}
              </p>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              {t('fortiva.requalify.modal.confirmQuestion')}
            </p>

            {/* Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <SecondaryOutlineButton
                onClick={onCancel}
                className="!px-6"
                data-testid="modal-cancel"
              >
                {t('fortiva.requalify.modal.cancelButton')}
              </SecondaryOutlineButton>
              <PrimaryButton onClick={onConfirm} className="!px-6" data-testid="modal-confirm">
                {t('fortiva.requalify.modal.confirmButton')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default function FinancingRequalify() {
  const { t } = useTranslation();
  document.title = t('fortiva.requalify.pageTitle');
  const navigate = useNavigate();
  const scroll = useScrollTop();
  const queryClient = useQueryClient();

  const [showSsn, setShowSsn] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(null);
  const [requestId, setRequestId] = React.useState('');
  const [requalifyPolling, setRequalifyPolling] = React.useState(false);
  const [isPollingLoading, setIsPollingLoading] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [pendingFormData, setPendingFormData] = React.useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useRequalifyForm();

  const { mutateAsync: requalifyApplication, isLoading: isRequalifyLoading } =
    useRequalifyApplication();

  const {
    isSuccess: isGetAppSuccess,
    isLoading: isGetAppLoading,
    data: getAppData,
  } = useGetApplication(requestId, requalifyPolling ? 3000 : false);

  const ssn = watch('socialSecurityNumber');
  React.useEffect(() => {
    setValue('socialSecurityNumber', ssn?.replace(/\D+/g, ''), { shouldValidate: true });
  }, [setValue, ssn]);

  // Handle polling response
  React.useEffect(() => {
    if (isGetAppSuccess && requalifyPolling && getAppData?.next_action) {
      // Store app data in session storage
      sessionStorage.setItem(constants.SESS_STORAGE_APPLICATION_KEY, JSON.stringify(getAppData));
      queryClient.invalidateQueries(cacheKeys.financingNextStep);

      // Stop polling and reset
      setRequestId('');
      setRequalifyPolling(false);
      setIsPollingLoading(false);

      // Navigate to financing redirect to handle the next action
      navigate({ to: createFinancingPath, search: true, replace: true });
    }
  }, [isGetAppSuccess, getAppData, requalifyPolling, navigate, queryClient]);

  // Update loading state when polling starts
  React.useEffect(() => {
    if (isGetAppLoading) {
      setIsPollingLoading(true);
    }
  }, [isGetAppLoading]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      setRequestId('');
      queryClient.removeQueries(cacheKeys.fortivaGetApplication);
    };
  }, [queryClient]);

  const toggleSsnDisplay = () => {
    setShowSsn(!showSsn);
  };

  const handleFormSubmit = (formData) => {
    // Store form data and show confirmation modal
    setPendingFormData(formData);
    setShowConfirmModal(true);
  };

  const handleConfirmRequalify = async () => {
    setShowConfirmModal(false);

    if (!pendingFormData) return;

    setSubmitError(null);
    setRequestId(''); // Clear old requestId first
    scroll.top();

    try {
      setRequalifyPolling(true);
      const {
        headers: { request_id },
      } = await requalifyApplication({
        customer_identity_number: pendingFormData.socialSecurityNumber,
      });

      setRequestId(request_id);
    } catch (error) {
      setRequalifyPolling(false);
      setSubmitError(error?.response?.data?.message || t('status.error.requalifyFailed'));
    }

    setPendingFormData(null);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setPendingFormData(null);
  };

  const isLoading = isRequalifyLoading || isPollingLoading;

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="fortiva"
    >
      {isRequalifyLoading && <Loader message={t('status.loading.requalifyApplication')} />}
      {isPollingLoading && <Loader message={t('status.loading.financingApplication')} />}

      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmRequalify}
        onCancel={handleCancelConfirm}
        t={t}
      />

      <div className="max-w-4xl mx-auto mt-6">
        {/* Header Card */}
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaSnowflake className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white" data-testid="title-credit-freeze">
                  {t('fortiva.requalify.title')}
                </h1>
                <p className="text-amber-100 text-sm">{t('fortiva.requalify.subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6">{t('fortiva.requalify.description')}</p>

            {/* Step 1: Phone Card, Step 2: Confirmation */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </span>
                <span className="font-medium text-gray-700">
                  {t('fortiva.requalify.step1Instruction')}
                </span>
              </div>
              <PhoneCard
                title={t('fortiva.requalify.transUnionTitle')}
                phone={t('fortiva.requalify.phoneNumbers.transUnion')}
                description={t('fortiva.requalify.transUnionDescription')}
              />
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </span>
                <span className="font-medium text-gray-700">
                  {t('fortiva.requalify.step2newInstruction')}
                </span>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                    <FaUserCheck className="text-blue-600 text-lg" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {t('fortiva.requalify.confirmFreezeLifted')}
                    </p>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {t('fortiva.requalify.confirmFreezeLiftedDescription')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-6" />

            {/* Step 3: Re-qualify */}
            <div className="flex items-center gap-2 mb-4">
              <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                3
              </span>
              <span className="font-medium text-gray-700">
                {t('fortiva.requalify.step3Instruction')}
              </span>
            </div>

            {submitError && (
              <AlertError className="mb-4" data-testid="requalify-error">
                {submitError}
              </AlertError>
            )}

            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  {t('fortiva.requalify.ssnInstruction')}
                </p>
                <div className="max-w-md col-6">
                  <InputField
                    register={register}
                    name="socialSecurityNumber"
                    errors={errors}
                    onAppendClick={toggleSsnDisplay}
                    appendText={showSsn ? <FaEyeSlash /> : <FaEye />}
                    type={showSsn ? 'text' : 'password'}
                  />
                </div>
                <div className="max-w-md mt-4 col-6">
                  <PrimaryButton
                    type="submit"
                    block
                    data-testid="btn-requalify"
                    disabled={isLoading || !isValid}
                  >
                    {t('fortiva.requalify.submitButton')}
                  </PrimaryButton>
                </div>
              </div>
            </form>
          </div>
        </Card>
      </div>
    </Page>
  );
}
